// @flow

/**
 * Country list and address-format configuration for international addresses.
 *
 * ADDRESS_COUNTRIES — ordered list of supported countries (code + label).
 * getAddressFieldConfig(countryCode) — returns locale-aware field labels,
 *   placeholders, and validation flags for the given ISO 3166-1 alpha-2 code.
 */

export type AddressFieldConfig = {
  stateLabel: string,
  statePlaceholder: string,
  stateRequired: boolean,
  zipLabel: string,
  zipPlaceholder: string,
  zipRequired: boolean,
};

export type Country = {
  code: string,
  label: string,
};

// ~40 most commonly used countries in a manufacturing/B2B context.
export const ADDRESS_COUNTRIES: Array<Country> = [
  { code: 'US', label: 'United States' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'CA', label: 'Canada' },
  { code: 'AU', label: 'Australia' },
  { code: 'IE', label: 'Ireland' },
  { code: 'DE', label: 'Germany' },
  { code: 'FR', label: 'France' },
  { code: 'NL', label: 'Netherlands' },
  { code: 'BE', label: 'Belgium' },
  { code: 'IT', label: 'Italy' },
  { code: 'ES', label: 'Spain' },
  { code: 'PT', label: 'Portugal' },
  { code: 'SE', label: 'Sweden' },
  { code: 'NO', label: 'Norway' },
  { code: 'DK', label: 'Denmark' },
  { code: 'FI', label: 'Finland' },
  { code: 'CH', label: 'Switzerland' },
  { code: 'AT', label: 'Austria' },
  { code: 'PL', label: 'Poland' },
  { code: 'CZ', label: 'Czech Republic' },
  { code: 'NZ', label: 'New Zealand' },
  { code: 'ZA', label: 'South Africa' },
  { code: 'SG', label: 'Singapore' },
  { code: 'HK', label: 'Hong Kong' },
  { code: 'JP', label: 'Japan' },
  { code: 'CN', label: 'China' },
  { code: 'IN', label: 'India' },
  { code: 'BR', label: 'Brazil' },
  { code: 'MX', label: 'Mexico' },
  { code: 'AR', label: 'Argentina' },
  { code: 'AE', label: 'United Arab Emirates' },
  { code: 'SA', label: 'Saudi Arabia' },
  { code: 'IL', label: 'Israel' },
  { code: 'TR', label: 'Turkey' },
  { code: 'RU', label: 'Russia' },
  { code: 'UA', label: 'Ukraine' },
  { code: 'PH', label: 'Philippines' },
  { code: 'MY', label: 'Malaysia' },
  { code: 'TH', label: 'Thailand' },
  { code: 'ID', label: 'Indonesia' },
];

// Per-country address field configuration.
// Countries not listed fall through to the default case.
const COUNTRY_CONFIGS: { [string]: AddressFieldConfig } = {
  US: {
    stateLabel: 'State',
    statePlaceholder: 'e.g. CA',
    stateRequired: true,
    zipLabel: 'Zip Code',
    zipPlaceholder: '12345',
    zipRequired: true,
  },
  GB: {
    stateLabel: 'County',
    statePlaceholder: 'e.g. Surrey',
    stateRequired: false,
    zipLabel: 'Post Code',
    zipPlaceholder: 'SW1A 1AA',
    zipRequired: true,
  },
  CA: {
    stateLabel: 'Province',
    statePlaceholder: 'e.g. ON',
    stateRequired: true,
    zipLabel: 'Postal Code',
    zipPlaceholder: 'A1A 1A1',
    zipRequired: true,
  },
  AU: {
    stateLabel: 'State / Territory',
    statePlaceholder: 'e.g. NSW',
    stateRequired: true,
    zipLabel: 'Postcode',
    zipPlaceholder: '2000',
    zipRequired: true,
  },
  NZ: {
    stateLabel: 'Region',
    statePlaceholder: 'e.g. Auckland',
    stateRequired: false,
    zipLabel: 'Postcode',
    zipPlaceholder: '1010',
    zipRequired: true,
  },
  IE: {
    stateLabel: 'County',
    statePlaceholder: 'e.g. Dublin',
    stateRequired: false,
    zipLabel: 'Eircode',
    zipPlaceholder: 'D01 A2B3',
    zipRequired: false,
  },
  IN: {
    stateLabel: 'State',
    statePlaceholder: 'e.g. Maharashtra',
    stateRequired: true,
    zipLabel: 'PIN Code',
    zipPlaceholder: '400001',
    zipRequired: true,
  },
  BR: {
    stateLabel: 'State',
    statePlaceholder: 'e.g. SP',
    stateRequired: true,
    zipLabel: 'CEP',
    zipPlaceholder: '01310-100',
    zipRequired: true,
  },
  MX: {
    stateLabel: 'State',
    statePlaceholder: 'e.g. CDMX',
    stateRequired: true,
    zipLabel: 'Postal Code',
    zipPlaceholder: '06600',
    zipRequired: true,
  },
  JP: {
    stateLabel: 'Prefecture',
    statePlaceholder: 'e.g. Tokyo',
    stateRequired: true,
    zipLabel: 'Postal Code',
    zipPlaceholder: '100-0001',
    zipRequired: true,
  },
  CN: {
    stateLabel: 'Province',
    statePlaceholder: 'e.g. Guangdong',
    stateRequired: true,
    zipLabel: 'Postal Code',
    zipPlaceholder: '510000',
    zipRequired: true,
  },
};

const DEFAULT_CONFIG: AddressFieldConfig = {
  stateLabel: 'State / Region',
  statePlaceholder: 'State / Region',
  stateRequired: false,
  zipLabel: 'Postal Code',
  zipPlaceholder: 'Postal Code',
  zipRequired: false,
};

/**
 * Returns address field labels/placeholders/validation config for a given
 * ISO 3166-1 alpha-2 country code. Falls back to generic defaults for unknown
 * or omitted codes.
 */
export function getAddressFieldConfig(countryCode: any): AddressFieldConfig {
  if (!countryCode) return DEFAULT_CONFIG;
  const code = typeof countryCode === 'object' ? countryCode?.value : countryCode;
  if (!code || typeof code !== 'string') return DEFAULT_CONFIG;
  return COUNTRY_CONFIGS[code.toUpperCase()] || DEFAULT_CONFIG;
}

export default getAddressFieldConfig;
